import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  FormControl,
  DialogActions,
  Button,
  Box,
  AlertColor,
  CircularProgress,
  Select,
  MenuItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { getJwtTokenFromIdToken } from '../auth/AuthFanc';
import RequiredLabel from '../../atoms/RequiredLabel';
import React from 'react';
import ConfirmDialog from '../../molecules/ConfirmDialog';

const memoLengthLimit = 200;

interface VisitorAppRegistrationModalProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  setSnackbarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setAlertSeverity: React.Dispatch<React.SetStateAction<AlertColor>>;
  setSnackbarMessage: React.Dispatch<React.SetStateAction<string>>;
}

interface SdcBuilding {
  buildingName: string;
  buildingAddress: string;
}

interface Sdc {
  sdcUuid: string;
  sdcSerialNumber: string;
  doorPlace: string;
  building: SdcBuilding;
}

interface ValidationErrors {
  sdcUuid: string | null;
  memo: string | null;
}

const emptyValidationErrors: ValidationErrors = {
  sdcUuid: null,
  memo: null,
};

const VisitorAppRegistrationModal: React.FC<
  VisitorAppRegistrationModalProps
> = ({
  open,
  setOpen,
  setSnackbarOpen,
  setAlertSeverity,
  setSnackbarMessage,
}) => {
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [sdcMap, setSdcMap] = React.useState<Map<string, Sdc>>(
    new Map<string, Sdc>()
  );
  const [targetSdc, setTargetSdc] = useState<Sdc | null>(null);
  const [memo, setMemo] = useState<string>('');
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    emptyValidationErrors
  );
  const [isSubmitInProgress, setIsSubmitInProgress] =
    React.useState<boolean>(false);

  const [openConfirm, setOpenConfirm] = React.useState(false);

  const fetchSdcs = () => {
    setIsLoading(true);
    getJwtTokenFromIdToken().then((token) => {
      // fetch SDCs registered to any building
      fetch(
        process.env.REACT_APP_API_ENDPOINT + '/sdcs?isRegisteredAsDoor=true',
        {
          method: 'GET',
          headers: {
            Authorization: token,
          },
        }
      )
        .then((response) => {
          return response.json();
        })
        .then((body) => {
          let tmpMap = new Map<string, Sdc>();
          for (let i = 0; i < body.sdcs.length; i++) {
            const sdc = (body.sdcs as Sdc[])[i];
            tmpMap.set(sdc.sdcUuid, sdc);
          }
          setSdcMap(tmpMap);
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          setIsLoading(false);
        });
    });
  };

  const handleValidation = () => {
    const errors: ValidationErrors = {
      sdcUuid: !targetSdc ? 'SDCを指定してください' : null,
      memo:
        memo.length > memoLengthLimit
          ? `${memoLengthLimit}文字以内で入力してください`
          : null,
    };
    setValidationErrors(errors);
    return !Object.values(errors).some((error) => error !== null);
  };

  const handleClose = () => {
    // reset stateful values
    setTargetSdc(null);
    setMemo('');
    setValidationErrors(emptyValidationErrors);

    // close this modal
    setOpen(false);
  };

  const handleSubmit = async () => {
    // check if any error exists
    if (!handleValidation()) {
      return;
    }

    setIsSubmitInProgress(true);

    await getJwtTokenFromIdToken().then((token) => {
      // register visitor app
      fetch(process.env.REACT_APP_API_ENDPOINT + '/visitors', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({
          sdcUuid: targetSdc!.sdcUuid,
          memo: memo,
        }),
      })
        .then((response) => {
          if (response.status !== 200) {
            console.error(response.status);
            setSnackbarMessage(
              '訪問者端末の登録に失敗しました。サーバ管理者に問い合わせてください。'
            );
            setAlertSeverity('error');
            setSnackbarOpen(true);
            return null;
          }
          return response.json();
        })
        .then((body) => {
          if (body !== null) {
            setOpenConfirm(true);
          }
        })
        .catch((e) => {
          console.error(e);
          setSnackbarMessage(
            '訪問者端末の登録に失敗しました。サーバ管理者に問い合わせてください。'
          );
          setAlertSeverity('error');
          setSnackbarOpen(true);
        })
        .finally(() => {
          setOpen(false);
          setIsSubmitInProgress(false);
        });
    });
  };

  React.useEffect(() => {
    if (open) {
      // fetch sdcs once it is opened
      fetchSdcs();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="form-dialog-title">新規訪問者端末登録</DialogTitle>
        <DialogContent sx={{ px: 5 }}>
          {isLoading ? (
            <CircularProgress />
          ) : (
            <>
              <Box
                // targetSdc box
                sx={{ mb: 2 }}
              >
                <Box
                  // title and error text box
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <Typography component={'span'} variant="body1" sx={{ mr: 1 }}>
                    紐づけるSDC
                    <RequiredLabel />
                  </Typography>
                  <Typography component={'span'} variant="body2" color={'red'}>
                    {validationErrors.sdcUuid}
                  </Typography>
                </Box>
                <FormControl
                  error={!!validationErrors.sdcUuid}
                  size="small"
                  fullWidth
                  sx={{ mt: 0, mb: 0.5 }}
                >
                  <Select
                    id="target-sdc-uuid-select"
                    value={targetSdc ? targetSdc.sdcUuid : ''}
                    defaultValue=""
                    onChange={(event) => {
                      setTargetSdc(sdcMap.get(event.target.value)!);
                    }}
                  >
                    {Array.from(sdcMap.keys()).map((sdcUuid) => (
                      <MenuItem
                        key={sdcMap.get(sdcUuid)!.sdcSerialNumber}
                        value={sdcUuid}
                      >
                        <ListItemText
                          primary={sdcMap.get(sdcUuid)!.sdcSerialNumber}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box sx={{ display: 'flex', pl: 5 }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography>ドア設置場所：</Typography>
                    <Typography>ドア設置物件名：</Typography>
                    <Typography>ドア設置物件住所：</Typography>
                  </Box>
                  <Box sx={{ flexGrow: 3 }}>
                    <Typography>
                      {targetSdc ? targetSdc.doorPlace : '-'}
                    </Typography>
                    <Typography>
                      {targetSdc ? targetSdc.building.buildingName : '-'}
                    </Typography>
                    <Typography>
                      {targetSdc ? targetSdc.building.buildingAddress : '-'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
              // memo box
              >
                <Box
                  // title and error text box
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <Typography variant="body1" sx={{ mr: 1 }}>
                    メモ
                  </Typography>
                  <Typography variant="body2" color={'red'}>
                    {validationErrors.memo}
                  </Typography>
                </Box>
                <TextField
                  id="memo"
                  type="text"
                  fullWidth
                  value={memo}
                  onChange={(e) => {
                    setMemo(e.target.value);
                  }}
                  error={!!validationErrors.memo}
                  size="small"
                  sx={{ mt: 0 }}
                />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            キャンセル
          </Button>
          <Button onClick={handleSubmit} color="primary">
            {isSubmitInProgress ? <CircularProgress size={20} /> : '登録'}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={openConfirm}
        onClose={() => {
          setOpenConfirm(false);
        }}
        onConfirm={() => {
          window.location.reload();
        }}
        title="確認"
        description="訪問者端末の新規登録が完了しました。"
        showCancelButton={false}
      />
    </>
  );
};

export default VisitorAppRegistrationModal;
